import { computeWeightedComplexity, getComplexityFactors, type BuildIntegrationCategory } from './common';

export function estimateMaintenanceTimeAndCost(
    data: {
        categories: BuildIntegrationCategory[];
        numberOfNewIntegrations: number;
        // yearsInBusiness: number;
        developersAnnualSalary: number;
        supportAnnualSalary?: number;
        productManagersAnnualSalary: number;
        devOpsAnnualSalary: number;
        numberOfExistingIntegrations: number;
        numberOfAPICallsPerMonth: number;
        ticketsPerIntegrationPerYear?: number; // default 12
        hoursPerTicket?: number; // default 1.5
        partnerFeesMaintenance?: number; // recurring annual
    },
    debug = false
): {
    numberOfDevelopers: number;
    numberOfSupport: number;
    numberOfProductManagers: number;
    numberOfDevOps: number;
    monthlyCost: number;
    annualCost: number;
} {
    if (debug) {
        console.log('[MAINTENANCE] data', JSON.stringify(data, null, 2));
    }
    const numberOfIntegrations = data.numberOfNewIntegrations + data.numberOfExistingIntegrations;
    if (debug) {
        console.log('[MAINTENANCE] numberOfIntegrations', numberOfIntegrations);
    }

    if (numberOfIntegrations <= 0) {
        return {
            numberOfDevelopers: 0,
            numberOfSupport: 0,
            numberOfProductManagers: 0,
            numberOfDevOps: 0,
            monthlyCost: 0,
            annualCost: 0,
        };
    }

    const numberOfDevelopers = calculateNumberOfDevelopersForMaintenance(data.categories, numberOfIntegrations, debug);
    if (debug) {
        console.log('[MAINTENANCE] numberOfDevelopers', numberOfDevelopers);
    }
    const numberOfSupport = calculateNumberOfSupportForMaintenance(
        data.categories,
        numberOfIntegrations,
        data.ticketsPerIntegrationPerYear ?? 12,
        data.hoursPerTicket ?? 1.5,
        debug
    );
    if (debug) {
        console.log('[MAINTENANCE] numberOfSupport', numberOfSupport);
    }
    const numberOfProductManagers = calculateNumberOfProductManagersForMaintenance(data.categories, numberOfIntegrations);
    if (debug) {
        console.log('[MAINTENANCE] numberOfProductManagers', numberOfProductManagers);
    }
    const numberOfDevOps = calculateNumberOfDevOps(data.numberOfAPICallsPerMonth);
    if (debug) {
        console.log('[MAINTENANCE] numberOfDevOps', numberOfDevOps);
    }

    const partnerFeesMaintenance = data.partnerFeesMaintenance ?? 0;
    const annualCost =
        numberOfDevelopers * data.developersAnnualSalary +
        numberOfSupport * (data.supportAnnualSalary ?? 0) +
        numberOfProductManagers * data.productManagersAnnualSalary +
        numberOfDevOps * data.devOpsAnnualSalary +
        partnerFeesMaintenance;
    if (debug) {
        console.log('[MAINTENANCE] annualCost', annualCost);
    }
    const monthlyCost = annualCost / 12;
    if (debug) {
        console.log('[MAINTENANCE] monthlyCost', monthlyCost);
    }

    return {
        numberOfDevelopers: Number(numberOfDevelopers),
        numberOfSupport: Number(Number(numberOfSupport).toFixed(1)),
        numberOfProductManagers: Number(numberOfProductManagers),
        numberOfDevOps: Number(numberOfDevOps),
        monthlyCost: monthlyCost ? Number(monthlyCost.toFixed(2)) : 0,
        annualCost: annualCost ? Number(annualCost.toFixed(2)) : 0,
    };
}

function calculateNumberOfDevelopersForMaintenance(categories: BuildIntegrationCategory[], numberOfIntegrations: number, debug = false) {
    const complexityFactor = computeWeightedComplexity(categories, 'maintain');

    if (debug) {
        console.log('[MAINTENANCE] complexityFactor', complexityFactor);
    }

    // Developer headcount scales sublinearly with num integrations; tuned roughly to plan
    const numberOfIntegrationsFactor = Math.pow(numberOfIntegrations / 4, 0.8);

    if (debug) {
        console.log('[MAINTENANCE] numberOfIntegrationsFactor', numberOfIntegrationsFactor);
    }

    return numberOfIntegrationsFactor * complexityFactor;
}

function calculateNumberOfProductManagersForMaintenance(categories: BuildIntegrationCategory[], totalNumberOfIntegrations: number) {
    const complexityFactor = computeWeightedComplexity(categories, 'maintain');

    // Base number of PMs scales slowly with integrations
    const basePMs = Math.max(0.5, totalNumberOfIntegrations / 300);

    return basePMs * complexityFactor;
}

function calculateNumberOfDevOps(numberOfAPICallsPerMonth: number) {
    // =IF(B4<=10000, 1%, IF(B4<=100000, 5%, IF(B4<=1000000, 10%, 20%)))
    return numberOfAPICallsPerMonth <= 10_000
        ? 0.01
        : numberOfAPICallsPerMonth <= 100_000
        ? 0.05
        : numberOfAPICallsPerMonth <= 1_000_000
        ? 0.1
        : numberOfAPICallsPerMonth / 5_000_000;
}

function calculateNumberOfSupportForMaintenance(
    categories: BuildIntegrationCategory[],
    totalIntegrations: number,
    ticketsPerIntegrationPerYear: number,
    hoursPerTicket: number,
    debug = false
) {
    const annualSupportHours = totalIntegrations * ticketsPerIntegrationPerYear * hoursPerTicket;
    const fte = annualSupportHours / (40 * 52);
    // Escalate slightly with complexity average
    const complexityFactor = computeWeightedComplexity(categories, 'maintain');
    const result = fte * (0.9 + 0.1 * complexityFactor);
    if (debug) {
        console.log('[MAINTENANCE] supportFTE', { annualSupportHours, fte, complexityFactor, result });
    }
    return result;
}
